<template>
  <v-container id="location-details" fluid>
    <v-card v-if="loadedSettings" outlined>
      <validation-observer ref="form">
        <v-form>
          <v-tabs grow show-arrows>
            <v-tab>{{ $t('customizations.ebooking') }}</v-tab>
            <v-tab>{{ $t('customizations.ebookingStock') }}</v-tab>
            <v-tab>{{ $t('customizations.ebookingBasket') }}</v-tab>
            <v-tab>{{ $t('customizations.ebookingOptins') }}</v-tab>
            <v-tab>{{ $t('customizations.ebookingDelay') }}</v-tab>
            <v-tab-item>
              <v-card class="mb-5" elevation="0">
                <v-container fluid>
                  <v-row class="align-center">
                    <v-col class="pb-0">
                      <v-text-field
                          v-model="loadedSettings.logoUrl"
                          :disabled="loading"
                          :error-messages="errors"
                          :label="$t('customizations.logoURL')"
                          class="text-uppercase"
                          dense
                          hide-details="auto"
                          outlined
                          required
                      ></v-text-field>
                    </v-col>
                  </v-row>
                  <v-row class="align-center">
                    <v-col class="pb-0">
                      <validation-provider
                          v-slot="{ errors }"
                          name="distance"
                          rules="min_value:1|integer|required">
                        <v-text-field
                            v-model="loadedSettings.maxDistance"
                            :error-messages="errors"
                            :label="$t('customizations.maxDistance')"
                            dense
                            hide-details="auto"
                            outlined
                            required
                            type="number"
                        ></v-text-field>
                      </validation-provider>
                    </v-col>
                  </v-row>
                  <v-row class="align-center">
                    <v-col class="pb-0">
                      <v-text-field
                          v-model="loadedSettings.primaryColor"
                          :label="$t('customizations.primaryColor')"
                          dense
                          hide-details="auto"
                          outlined/>
                    </v-col>
                  </v-row>
                  <v-row class="align-center">
                    <v-col class="pb-0 mb-0">
                      <v-select
                          v-model="loadedSettings.font"
                          :items="fontsArray"
                          :label="$t('customizations.font')"
                          dense
                          hide-details="auto"
                          outlined/>
                    </v-col>
                  </v-row>
                  <v-card class="mt-3" elevation="0" outlined>
                    <v-container fluid>
                      <v-row class="align-center">
                        <v-col cols="12">
                          <language-select
                              :key="key"
                              v-model="languageModel.CGUUrl"
                              :allow-add-language="true"
                              :filter="true"
                              :language-to-filter="filteredCguLanguage"
                              @deleteLanguage="deleteCguLanguage"
                              @languageAdded="cguLanguageAdded"
                          />
                        </v-col>
                        <v-col>
                          <validation-provider
                              v-slot="{ errors }"
                              :rules="{required: languageModel.CGUUrl !== '' }"
                              name="CGU URL"
                          >
                            <v-text-field
                                v-model="loadedSettings.CGUUrl[languageModel.CGUUrl]"
                                :disabled="loading"
                                :error-messages="errors"
                                :label="$t('customizations.CGUURL')"
                                class="text-uppercase"
                                dense
                                hide-details="auto"
                                outlined
                                required
                            ></v-text-field>
                          </validation-provider>
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-card>
                </v-container>
              </v-card>
            </v-tab-item>
            <v-tab-item>
              <v-card class="mb-5" elevation="0">
                <v-container fluid>
                  <v-row class="align-center">
                    <v-col class="pb-0">
                      <v-switch
                          v-model="loadedSettings.displayStocks"
                          :label="$t('customizations.displayStocks')"
                          dense/>
                      <v-switch
                          v-model="loadedSettings.displayShopWithoutStock"
                          :label="$t('customizations.displayShopWithoutStock')"
                          class="mt-0 pt-0"
                          dense/>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-container fluid>
                      <v-card :disabled="!loadedSettings.displayStocks">
                        <v-row class="align-center">
                          <v-col class="pb-0">
                            <validation-provider
                                v-slot="{ errors }"
                                :name="$t('customizations.stockLevel.med')"
                                rules="min_value:2|integer"
                            >
                              <v-text-field
                                  v-model="loadedSettings.stockLevel.medium"
                                  :errors="errors"
                                  :label="$t('customizations.stockLevel.med')"
                                  dense
                                  hide-details="auto"
                                  outlined
                                  type="number"/>
                            </validation-provider>
                          </v-col>
                        </v-row>
                        <v-row class="align-center">
                          <v-col class="pb-0">
                            <validation-provider
                                v-slot="{ errors }"
                                :name="$t('customizations.stockLevel.max')"
                                rules="min_value:3|integer"
                            >
                              <v-text-field
                                  v-model="loadedSettings.stockLevel.max"
                                  :errors="errors"
                                  :label="$t('customizations.stockLevel.max')"
                                  dense
                                  hide-details="auto"
                                  outlined
                                  type="number"/>
                            </validation-provider>
                          </v-col>
                        </v-row>
                      </v-card>
                    </v-container>
                  </v-row>
                </v-container>
              </v-card>
            </v-tab-item>
            <v-tab-item>
              <v-card class="mb-5" elevation="0">
                <v-container fluid>
                  <v-row class="align-center">
                    <v-col class="pb-0">
                      <validation-provider
                          v-slot="{ errors }"
                          :name="$t('customizations.basket.productQuantity')"
                          rules="integer"
                      >
                        <v-text-field
                            v-model="loadedSettings.basket.maxProductQuantity"
                            :errors="errors"
                            :label="$t('customizations.basket.productQuantity')"
                            dense
                            hide-details="auto"
                            outlined
                            type="number"/>
                      </validation-provider>
                    </v-col>
                  </v-row>
                  <v-row class="align-center">
                    <v-col class="pb-0">
                      <validation-provider
                          v-slot="{ errors }"
                          :name="$t('customizations.basket.productLines')"
                          rules="integer"
                      >
                        <v-text-field
                            v-model="loadedSettings.basket.maxProductLines"
                            :errors="errors"
                            :label="$t('customizations.basket.productLines')"
                            dense
                            hide-details="auto"
                            outlined
                            type="number"/>
                      </validation-provider>
                    </v-col>
                  </v-row>
                  <v-row class="align-center">
                    <v-col class="pb-0">
                      <validation-provider
                          v-slot="{ errors }"
                          :name="$t('customizations.basket.maxGlobalProductQuantity')"
                          rules="integer"
                      >
                        <v-text-field
                            v-model="loadedSettings.basket.maxGlobalProductQuantity"
                            :errors="errors"
                            :label="$t('customizations.basket.maxGlobalProductQuantity')"
                            dense
                            hide-details="auto"
                            outlined
                            type="number"/>
                      </validation-provider>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card>
            </v-tab-item>
            <v-tab-item>
              <v-card class="mb-5" elevation="0">
                <v-container>
                  <v-row class="align-center">
                    <v-col class="p-0" cols="12">
                      <v-card elevation="0" outlined>
                        <v-card-text>
                          <v-switch
                              v-model="loadedSettings.optins.global"
                              :label="$t('customizations.showOptins')"
                              dense
                              @change="globalOptin"
                          />
                          <v-list class="pt-0">
                            <v-list-item>
                              <v-switch
                                  v-model="loadedSettings.optins.email"
                                  :disabled="!loadedSettings.optins.global"
                                  :label=" $t('customizations.showOptinsEmail')"
                                  class="mt-0 pt-0"
                                  dense
                              />
                            </v-list-item>
                            <v-list-item>
                              <v-switch
                                  v-model="loadedSettings.optins.sms"
                                  :disabled="!loadedSettings.optins.global"
                                  :label="$t('customizations.showOptinsSms')"
                                  class="mt-0 pt-0"
                                  dense
                              />
                            </v-list-item>
                            <v-list-item>
                              <v-switch
                                  v-model="loadedSettings.optins.phone"
                                  :disabled="!loadedSettings.optins.global"
                                  :label=" $t('customizations.showOptinsPhone')"
                                  class="mt-0 pt-0"
                                  dense
                              />
                            </v-list-item>
                          </v-list>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <language-select
                          :key="key"
                          v-model="languageModel.optins"
                          :allow-add-language="true"
                          :disabled="!loadedSettings.optins.sms && !loadedSettings.optins.email && !loadedSettings.optins.phone"
                          :filter="true"
                          :language-to-filter="toolTipFilter"
                          @deleteLanguage="deleteToolTipLanguage"
                          @languageAddes="toolTipLanguageAdded"
                      />
                    </v-col>
                    <v-col cols="12">
                      <validation-provider
                          v-slot="{ errors }"
                          :rules="{required: languageModel.optins !== ''}"
                          name="CGU URL"
                      >
                        <v-textarea
                            v-model="loadedSettings.optins.tooltipText[languageModel.optins]"
                            :disabled="!loadedSettings.optins.sms && !loadedSettings.optins.email && !loadedSettings.optins.phone"
                            :error-messages="errors"
                            :label="$t('customizations.tooltipOptins')"
                            dense
                            outlined
                        />
                      </validation-provider>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card>
            </v-tab-item>
            <v-tab-item>
              <v-card class="mb-5" elevation="0">
                <v-container>
                  <v-row class="align-center">
                    <v-col class="d-flex justify-center" cols="12">
                      <v-card-title>{{ $t('customizations.customerPickup') }}</v-card-title>
                    </v-col>
                    <v-col cols="6">
                      <v-autocomplete
                          v-model="loadedSettings.maximumPickupDelay.duration"
                          :error-messages="errors"
                          :items="durationList"
                          :label="$t('customizations.duration')"
                          item-text="text"
                          item-value="value"
                          required>
                      </v-autocomplete>
                    </v-col>
                    <v-col cols="6">
                      <validation-provider
                          v-slot="{ errors }"
                          :name="$t('customizations.delay')"
                          rules="integer"
                      >
                        <v-text-field
                            v-model="loadedSettings.maximumPickupDelay.delay"
                            :errors="errors"
                            :label="$t('customizations.delay')"
                            dense
                            hide-details="auto"
                            outlined
                            type="number"/>
                      </validation-provider>
                    </v-col>
                  </v-row>
                  <v-row class="align-center">
                    <v-col class="d-flex justify-center" cols="12">
                      <v-card-title>{{ $t('customizations.storeConfirmation') }}</v-card-title>
                    </v-col>
                    <v-col cols="6">
                      <v-autocomplete
                          v-model="loadedSettings.maximumStoreConfirmationDelay.duration"
                          :error-messages="errors"
                          :items="durationList"
                          :label="$t('customizations.duration')"
                          item-text="text"
                          item-value="value"
                          required>
                      </v-autocomplete>
                    </v-col>
                    <v-col cols="6">
                      <validation-provider
                          v-slot="{ errors }"
                          :name="$t('customizations.storeConfirmation')"
                          rules="integer"
                      >
                        <v-text-field
                            v-model="loadedSettings.maximumStoreConfirmationDelay.delay"
                            :errors="errors"
                            :label="$t('customizations.delay')"
                            dense
                            hide-details="auto"
                            outlined
                            type="number"/>
                      </validation-provider>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card>
            </v-tab-item>
          </v-tabs>
        </v-form>
      </validation-observer>
      <v-container fluid>
        <v-row justify="center">
          <v-btn
            :loading="loading"
            class="ma-2 text-uppercase"
            color="primary"
            outlined
            @click="validate"
          ><span v-if="areNewSettings">{{ $t('settings.save') }}</span><span v-else>{{ $t('settings.update') }}</span></v-btn>
        </v-row>
      </v-container>
    </v-card>
  </v-container>
</template>

<script>

import { EventBus } from '../../../event-bus'
import { mapState } from 'vuex'
import { ValidationProvider, extend, ValidationObserver } from 'vee-validate'
import { integer, numeric } from 'vee-validate/dist/rules'
import LanguageSelect from '@/components/LanguageSelect'

extend('integer', {
  ...integer,
  message: 'floating point numbers are not allowed'
})
extend('numeric', { ...numeric })

export default {
  name: 'EbookingsCustomization',
  components: {
    'validation-provider': ValidationProvider,
    'validation-observer': ValidationObserver,
    LanguageSelect
  },
  data () {
    return {
      selectedCountry: undefined,
      areNewSettings: false,
      loading: false,
      loadedSettings: undefined,
      errors: [],
      languageModel: {
        CGUUrl: '',
        optins: ''
      },
      languages: [],
      fontsArray: [
        'Baskerville',
        'Gotham-Book',
        'Lato',
        'Arial',
        'Helvetica',
        'Verdana',
        'Calibri',
        'Futura',
        'Noto',
        'Gill sans',
        'Century Gothic',
        'Trebuchet MS',
        'Lucida Sans',
        'Franklin Gothic Medium',
        'Segoe UI',
        'Optima',
        'Comic Sans',
        'Proxima Nova',
        'Public Sans',
        'Garet',
        'Open Sans',
        'brownpro'
      ],
      key: Date.now()
    }
  },
  computed: {
    ...mapState(['currentStore', 'isCustomerService', 'isAdmin']),
    durationList () {
      return [
        { text: this.$t('customizations.days'), value: 'days' },
        { text: this.$t('customizations.month'), value: 'month' },
        { text: this.$t('customizations.hours'), value: 'hours' },
        { text: this.$t('customizations.hour'), value: 'hour' },
        { text: this.$t('customizations.day'), value: 'day' }
      ]
    },
    filteredCguLanguage () {
      return this.loadedSettings.CGUUrl ? Object.keys(this.loadedSettings.CGUUrl) : []
    },
    toolTipFilter () {
      return this.loadedSettings.optins.tooltipText ? Object.keys(this.loadedSettings.optins.tooltipText) : []
    }
  },
  mounted () {
    if (this.$route.params.id) {
      this.selectedCountry = this.$route.params.id
      this.getSettingsByCountry()
    }

  },
  methods: {
    getSettingsByCountry () { // get the settings of the country selected
      this.loading = true
      let myInit = {
        headers: {
          'Authorization': 'Bearer ' + this.$auth.accessToken
        }
      }
      this.$Amplify.API.get('ebookings', '/v1/int/ebookings/settings/' + this.selectedCountry, myInit).then((res) => {
        this.loadedSettings = {
          logoUrl: res.logo_url,
          CGUUrl: res.CGU_url || {},
          displayStocks: res.display_stocks,
          maxDistance: res.max_distance ? res.max_distance / 1000 : 100,
          font: res.font,
          primaryColor: res.primary_color,
          stockLevel: res.stock_level,
          basket: {
            maxProductQuantity: res.basket && res.basket.max_product_quantity ? res.basket.max_product_quantity : 2,
            maxProductLines: res.basket && res.basket.max_products_lines ? res.basket.max_products_lines : 2,
            maxGlobalProductQuantity: res.basket && res.basket.max_global_product_quantity ? res.basket.max_global_product_quantity : 2
          },
          id: res._id,
          displayShopWithoutStock: res.display_shop_without_stock,
          optins: {
            global: res.optins.global,
            sms: res.optins.sms,
            phone: res.optins.phone,
            email: res.optins.email,
            tooltipText: res.optins.tooltip_text ? res.optins.tooltip_text : {}
          },
          maximumPickupDelay: res.maximum_pickup_delay,
          maximumStoreConfirmationDelay: res.maximum_store_confirmation_delay
        }

        this.languageModel.optins = this.getFirstObjectKey(this.loadedSettings.optins.tooltipText)
        this.languageModel.CGUUrl = this.getFirstObjectKey(this.loadedSettings.CGUUrl)

        this.loading = false
      }).catch((e) => {
        //alert(e)
        console.log(e)
        if (e.response && e.response.status == 404) {
          this.areNewSettings = true
          this.loadedSettings = {
            logoUrl: '',
            CGUUrl: {},
            displayStocks: true,
            maxDistance: 100,
            font: 'Lato',
            primaryColor: '',
            stockLevel: { medium: 2, max: 3 },
            basket: {
              maxProductQuantity: 2,
              maxProductLines: 2,
              maxGlobalProductQuantity: 2
            },

            displayShopWithoutStock: true,
            optins: {
              global: true,
              sms: true,
              phone: true,
              email: true,
              tooltipText: {}
            },
            maximumPickupDelay: { delay: 2, duration: 'days' },
            maximumStoreConfirmationDelay: { delay: 1, duration: 'hour' }
          }
          EventBus.$emit('displaySnackbar', {
            display: true,
            text: this.$t('settings.noCountry'),
            color: 'warning'
          })
        } else {
          EventBus.$emit('displaySnackbar', {
            display: true,
            text: e.message,
            color: 'error'
          })
        }
        this.loading = false
      })
    },
    async validate () {   // validate form and send data in seconds to db
      let verifDelays = await this.$refs.form.validate()
      if (verifDelays) {

        var body = {
          country_code: this.selectedCountry,
          logo_url: this.loadedSettings.logoUrl,
          CGU_url: this.loadedSettings.CGUUrl,
          display_stocks: this.loadedSettings.displayStocks,
          max_distance: this.loadedSettings.maxDistance * 1000,
          font: this.loadedSettings.font,
          primary_color: this.loadedSettings.primaryColor,
          stock_level: this.loadedSettings.stockLevel,
          basket: {
            max_product_quantity: this.loadedSettings.basket.maxProductQuantity,
            max_products_lines: this.loadedSettings.basket.maxProductLines,
            max_global_product_quantity: this.loadedSettings.basket.maxGlobalProductQuantity
          },
          display_shop_without_stock: this.loadedSettings.displayShopWithoutStock,
          optins: {
            global: this.loadedSettings.optins.global,
            phone: this.loadedSettings.optins.phone,
            sms: this.loadedSettings.optins.sms,
            email: this.loadedSettings.optins.email,
            tooltip_text: this.loadedSettings.optins.tooltipText === '' ? undefined : this.loadedSettings.optins.tooltipText
          },
          maximum_pickup_delay: this.loadedSettings.maximumPickupDelay,
          maximum_store_confirmation_delay: this.loadedSettings.maximumStoreConfirmationDelay
        }

        let myInit = {
          headers: {
            'Authorization': 'Bearer ' + this.$auth.accessToken
          },
          body: body
        }
        if (this.areNewSettings) {
          this.createSettings(myInit)
        } else {   // if the country code exists in db, it will update values
          this.updateSettings(myInit)
        }
      } else {
        EventBus.$emit('displaySnackbar', {
          display: true,
          text: this.$t('settings.invalidSettings'),
          color: 'error'
        })
      }
    },
    createSettings (myInit) { // create settings for a country

      this.loading = true
      this.$Amplify.API.post('ebookings', '/v1/int/ebookings/settings', myInit).then((res) => {
        EventBus.$emit('displaySnackbar', {
          display: true,
          text: this.$t('settings.settingsCreated'),
          color: 'success'
        })
        this.loadedSettings.id = res._id
        this.areNewSettings = false
        this.loading = false
      }).catch((e) => {
        //alert(e)
        console.log(e)
        EventBus.$emit('displaySnackbar', {
          display: true,
          text: e.message,
          color: 'error'
        })
        this.loading = false
      })
    },
    updateSettings (myInit) { // update settings with new values
      this.loading = true
      this.$Amplify.API.put('ebookings', '/v1/int/ebookings/settings/' + this.$route.params.id, myInit).then((res) => {
        this.loadedSettings = {
          logoUrl: res.logo_url,
          CGUUrl: res.CGU_url,
          displayStocks: res.display_stocks,
          maxDistance: res.max_distance ? res.max_distance / 1000 : 100,
          font: res.font,
          primaryColor: res.primary_color,
          stockLevel: res.stock_level,
          basket: {
            maxProductQuantity: res.basket && res.basket.max_product_quantity ? res.basket.max_product_quantity : 2,
            maxProductLines: res.basket && res.basket.max_products_lines ? res.basket.max_products_lines : 2,
            maxGlobalProductQuantity: res.basket && res.basket.max_global_product_quantity ? res.basket.max_global_product_quantity : 2
          },
          id: res._id,
          displayShopWithoutStock: res.display_shop_without_stock,
          optins: {
            global: res.optins.global,
            sms: res.optins.sms,
            phone: res.optins.phone,
            email: res.optins.email,
            tooltipText: res.optins.tooltip_text ? res.optins.tooltip_text : {}
          },
          maximumPickupDelay: res.maximum_pickup_delay,
          maximumStoreConfirmationDelay: res.maximum_store_confirmation_delay
        }
        EventBus.$emit('displaySnackbar', {
          display: true,
          text: this.$t('settings.settingsUpdated'),
          color: 'success'
        })
        this.loading = false
      }).catch((e) => {
        EventBus.$emit('displaySnackbar', {
          display: true,
          text: e.message,
          color: 'error'
        })
        this.loading = false
      })
    },
    globalOptin () {
      if (!this.loadedSettings.optins.global) {
        this.loadedSettings.optins.sms = false
        this.loadedSettings.optins.email = false
        this.loadedSettings.optins.phone = false
      } else {
        this.loadedSettings.optins.sms = true
        this.loadedSettings.optins.email = true
        this.loadedSettings.optins.phone = true
      }
    },
    deleteToolTipLanguage (values) {
      this.$delete(this.loadedSettings.optins.tooltipText, values)
      this.languageModel.optins = this.getFirstObjectKey(this.loadedSettings.optins.tooltipText)
      this.key = Date.now()
    },
    deleteCguLanguage (values) {
      this.$delete(this.loadedSettings.CGUUrl, values)
      this.key = Date.now()
      this.languageModel.CGUUrl = this.getFirstObjectKey(this.loadedSettings.CGUUrl)
    },
    getFirstObjectKey (data) {
      let value = ''
      if (Object.keys(data).length) {
        value = Object.keys(data)[0]
      }
      console.log(value)
      return value
    },
    cguLanguageAdded (values) {
      this.languageModel.CGUUrl = values
      this.$set(this.loadedSettings.CGUUrl, values, null)
    },
    toolTipLanguageAdded (values) {
      this.languageModel.optins = values
      this.$set(this.loadedSettings.optins.tooltipText, values, null)
    }
  }
}
</script>
